import { DOCUMENT } from '@angular/common';
import { inject, LOCALE_ID } from '@angular/core';

import { isPostLocale, PostLocale } from './content/post.model';

/** Same host as `apps/landing/scripts/locales.mjs`. */
export const BASE_URL = 'https://ledgerly.download';

/**
 * Where each locale is served. English is the root and there is no `/en/`;
 * see the "Locale URLs" section of CLAUDE.md for why there is no negotiation.
 */
export const LOCALE_PATHS: Record<PostLocale, string> = {
  en: '/',
  fr: '/fr/',
  de: '/de/',
  it: '/it/',
  es: '/es/',
};

/**
 * The locale this build is serving.
 *
 * Read from `<html lang>` rather than LOCALE_ID: the per-locale index.html
 * carries the right value in every build, including during prerendering, and
 * it is the same attribute the header's language switcher already reads.
 */
export function currentLocale(): PostLocale {
  const lang = inject(DOCUMENT).documentElement.lang;
  if (isPostLocale(lang)) return lang;

  const fallback = inject(LOCALE_ID).split('-')[0];
  return isPostLocale(fallback) ? fallback : 'en';
}

/** An absolute URL for a path within a locale, e.g. `/fr/blog/some-post`. */
export function absoluteUrl(lang: PostLocale, path: string): string {
  const prefix = LOCALE_PATHS[lang].replace(/\/$/, '');
  return `${BASE_URL}${prefix}${path}`;
}

/** The blog index of a locale, as an app route (no locale prefix: base href does that). */
export const BLOG_PATH = '/blog';

export function postPath(slug: string): string {
  return `${BLOG_PATH}/${slug}`;
}

/** A date as the reader's language writes it, without needing Angular locale data. */
export function formatPostDate(date: string, lang: PostLocale): string {
  return new Intl.DateTimeFormat(lang, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`));
}
